import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MeasuredWallpaperCutout, {
  MeasuredWallpaperCutoutProps,
} from '../MeasuredWallpaperCutout/MeasuredWallpaperCutout';
import MeasuredWallpaperCuttingsData from '../../Types/MeasuredWallpaperCuttingsData';
import { throttle } from 'lodash-es';
import { translate } from '../../Services/translation';
import clsx from 'clsx';
import ProductPageProductPrice from '../ProductPage/ProductPageProductPrice';
import PriceModel from '../../Types/PriceModel';
import LoadingSpinner from '../LoadingSpinner';
import MeasuredWallpaperCalculator from '../ProductPage/MeasuredWallpaperCalculator';
import BuyButton from '../BuyButton/BuyButton';

interface MeasuredWallpaperStandaloneProps
  extends MeasuredWallpaperCutoutProps {
  articleNumber: string;
  panelOptions: {
    showPanel: boolean;
    showBuyButton: boolean;
    showPrice: boolean;
    showWallDropdowns: boolean;
  };
  name: string;
  customerId: string;
  channelId: string;
}

const MeasuredWallpaperStandalone = ({
  articleNumber,
  name,
  panelOptions,
  customerId,
  channelId,
  ...props
}: MeasuredWallpaperStandaloneProps) => {
  const [cuttingsData, setCuttingsData] = useState(
    {} as MeasuredWallpaperCuttingsData
  );
  const [flipHorizontally, setFlipHorizontally] = useState(false);
  const [showPriceLoadingSpinner, setShowPriceLoadingSpinner] =
    useState(false);
  const [priceModel, setPriceModel] = useState<PriceModel | undefined>();
  const [wallSize, setWallSize] = useState({
    width: props.wallWidth,
    height: props.wallHeight,
  });

  const postMessage = (eventName: string, data: any = {}) => {
    window.parent.postMessage(
      {
        event: eventName,
        ...data,
      },
      '*'
    );
  };

  const onAddToCartClick = () => {
    postMessage('ADD_TO_CART');
  };

  const submitCuttingsData = useCallback(
    throttle(
      (cuttingsData) => {
        postMessage('CUTTINGS_DATA_CHANGED', {
          cuttingsData,
        });
      },
      200,
      { leading: false, trailing: true }
    ),
    []
  );

  useEffect(() => {
    if (cuttingsData == ({} as MeasuredWallpaperCuttingsData)) return;
    submitCuttingsData({ ...cuttingsData, mirrored: flipHorizontally });
  }, [cuttingsData, flipHorizontally, submitCuttingsData]);

  useEffect(() => {
    let priceLoadingSpinnerTimeout: NodeJS.Timeout | undefined;
    const onMessage = async (e: MessageEvent) => {
      switch (e.data?.event) {
        case 'PRICE_LOADING':
          priceLoadingSpinnerTimeout = setTimeout(
            () => setShowPriceLoadingSpinner(true),
            200
          );
          break;
        case 'PRICE_LOADED':
          clearTimeout(priceLoadingSpinnerTimeout);
          setShowPriceLoadingSpinner(false);
          setPriceModel(e.data.priceModel);
          break;
        case 'WALL_SIZE_CHANGED':
          setWallSize({
            width: e.data.wallWidth,
            height: e.data.wallHeight,
          });
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', onMessage);

    return () => {
      clearTimeout(priceLoadingSpinnerTimeout);
      window.removeEventListener('message', onMessage);
    };
  }, []);

  const selectedWallSizeTooSmall = useMemo(
    () => (wallSize.width / 100) * (wallSize.height / 100) < 3,
    [wallSize.height, wallSize.width]
  );

  useEffect(() => {
    if (selectedWallSizeTooSmall) return;

    postMessage('SET_WALL_SIZE', {
      wallWidth: wallSize.width,
      wallHeight: wallSize.height,
    });
  }, [selectedWallSizeTooSmall, wallSize.width, wallSize.height]);

  return (
    <div
      className={clsx(
        'flex h-full w-full flex-col',
        panelOptions.showPanel !== false && 'lg:flex-row lg:flex-wrap'
      )}
    >
      <div
        className={clsx(
          'relative h-full w-full rounded-lg border-2 border-gray-100',
          panelOptions.showPanel !== false && 'lg:w-2/3'
        )}
      >
        <MeasuredWallpaperCutout
          {...props}
          wallWidth={wallSize.width}
          wallHeight={wallSize.height}
          onCuttingsDataChange={setCuttingsData}
          maxHeight={null}
          onFlipHorizontally={setFlipHorizontally}
          flipHorizontally={flipHorizontally}
        />
      </div>
      {panelOptions.showPanel !== false && (
        <div className="p-4 lg:w-1/3">
          <div className="flex flex-col gap-1">
            <p className="text-lg font-normal leading-6 text-gray-700">
              {translate('general.article-number')}: {articleNumber}
            </p>

            <h1 className="h3">{name}</h1>
          </div>
          <a className="mb-4 flex justify-between">
            {!showPriceLoadingSpinner && priceModel && (
              <ProductPageProductPrice
                priceModel={priceModel}
                showUnit={false}
              />
            )}
            {(showPriceLoadingSpinner || !priceModel) && (
              <LoadingSpinner />
            )}
          </a>
          {panelOptions.showWallDropdowns !== false && (
            <MeasuredWallpaperCalculator
              measuredWallpaper={props.measuredWallpaper}
              calculatorWidth={wallSize.width}
              calculatorHeight={wallSize.height}
              calculatorActive={true}
              calculatedSizeTooSmall={selectedWallSizeTooSmall}
              setCalculatorWidth={(width) =>
                setWallSize({ ...wallSize, width: width })
              }
              setCalculatorHeight={(height) =>
                setWallSize({ ...wallSize, height: height })
              }
            />
          )}
          {panelOptions.showBuyButton !== false && (
            <BuyButton
              disabled={
                selectedWallSizeTooSmall || showPriceLoadingSpinner
              }
              onClick={onAddToCartClick}
            >
              {translate('general.add-to-cart')}
            </BuyButton>
          )}
        </div>
      )}
    </div>
  );
};

export default MeasuredWallpaperStandalone;
